import { TriggerEventProps, TriggerEventType } from '../../../../models';
import { observe } from 'selector-observer';
import { EventStrategy } from './EventStrategy';
import { WorkflowContext } from '@samelogic/steps';

const triggerDOMEventMap = new Map<TriggerEventType, string>([
  ['ElementClick', 'click'],
  ['ElementMouseOver', 'mouseover']
]);

export type ElementEvent = Event;

/**
 * The shape of the payload for the ElementClick and ElementMouseOver events.
 * @public
 */
export interface ElementEventProps extends TriggerEventProps {
  event: 'ElementClick' | 'ElementMouseOver';
  /**
   * The CSS Selector of the element that was clicked or hovered over.
   */
  cssSelector: string;

  preventDefault?: boolean;

  /**
   * If the element had a default behavior, it will be delayed until the workflow is completed.
   */
  delayDefaultBehavior?: boolean;
}
/**
 * ElementEvents handles events fired off by a DOM element.
 * See https://developer.mozilla.org/en-US/docs/Web/API/Element#events
 */
export class ElementEventStrategy extends EventStrategy<ElementEventProps, ElementEvent> {
  private readonly domEvent: string;
  constructor(context: WorkflowContext, props: ElementEventProps) {
    super(context, props);

    const domEvent = triggerDOMEventMap.get(this.props.event);
    if (!domEvent) {
      throw Error(`eventType does not map to any DOM element event: ${this.props.event}`);
    }
    this.domEvent = domEvent;
  }

  public start(): void {
    const props = this.props;
    observe(props.cssSelector, {
      add: (el) => {
        el.addEventListener(this.domEvent, this.handleElementEvent);
      },
      remove: (el) => {
        el.removeEventListener(this.domEvent, this.handleElementEvent);
      }
    });
  }

  private handleElementEvent = (event: Event) => {
    //TODO: event.preventDefault(); and event.preventPropagation(); here when needed to stop default behavior
    if (this.props.preventDefault) {
      event.preventDefault();
    }

    this.onEventTriggered?.({
      condition: true,
      props: this.props,
      data: event
    });
  };
}
